// Guards de navigation pour l'authentification
import authService from '../services/authService'
import store from '../store'

/**
 * Guard pour les routes nécessitant une authentification
 */
export const requireAuth = async (to, from, next) => {
  try {
    // Vérifier si l'utilisateur est authentifié
    const isAuthenticated = authService.isAuthenticated()
    
    if (!isAuthenticated) {
      // Essayer d'initialiser l'authentification (refresh token)
      const initialized = await store.dispatch('login/initializeAuth')
      
      if (!initialized) {
        // Rediriger vers la page de login
        next({
          name: 'login',
          query: { redirect: to.fullPath }
        })
        return
      }
    }
    
    // L'utilisateur est authentifié, continuer
    next()
  } catch (error) {
    console.error('Erreur dans le guard d\'authentification:', error)
    next({
      name: 'login',
      query: { redirect: to.fullPath }
    })
  }
}

/**
 * Guard pour les routes accessibles uniquement aux utilisateurs non authentifiés
 */
export const requireGuest = async (to, from, next) => {
  try {
    const isAuthenticated = authService.isAuthenticated()
    
    if (isAuthenticated) {
      // L'utilisateur est déjà connecté, rediriger vers la page d'accueil
      next({ name: 'home' })
      return
    }
    
    // Essayer d'initialiser l'authentification au cas où
    const initialized = await store.dispatch('login/initializeAuth')
    
    if (initialized) {
      // L'utilisateur s'est reconnecté automatiquement
      next({ name: 'home' })
      return
    }
    
    // L'utilisateur n'est pas connecté, continuer
    next()
  } catch (error) {
    console.error('Erreur dans le guard guest:', error)
    // En cas d'erreur, laisser passer (mieux vaut permettre l'accès au login)
    next()
  }
}

/**
 * Guard pour vérifier les rôles utilisateur
 */
export const requireRole = (roles) => {
  return async (to, from, next) => {
    try {
      // D'abord vérifier l'authentification
      const isAuthenticated = authService.isAuthenticated()
      
      if (!isAuthenticated) {
        const initialized = await store.dispatch('login/initializeAuth')
        
        if (!initialized) {
          next({
            name: 'login',
            query: { redirect: to.fullPath }
          })
          return
        }
      }
      
      // Récupérer l'utilisateur actuel
      const user = authService.getCurrentUser()
      
      if (!user || !user.roles) {
        next({
          name: 'login',
          query: { redirect: to.fullPath }
        })
        return
      }
      
      // Vérifier si l'utilisateur a au moins un des rôles requis
      const hasRequiredRole = roles.some(role => user.roles.includes(role))
      
      if (!hasRequiredRole) {
        // L'utilisateur n'a pas les permissions nécessaires
        next({
          name: 'unauthorized',
          query: { redirect: to.fullPath }
        })
        return
      }
      
      // L'utilisateur a les permissions, continuer
      next()
    } catch (error) {
      console.error('Erreur dans le guard de rôle:', error)
      next({
        name: 'login',
        query: { redirect: to.fullPath }
      })
    }
  }
}

/**
 * Guard global pour initialiser l'authentification
 */
export const initializeAuth = async (to, from, next) => {
  try {
    // Initialiser l'authentification si ce n'est pas déjà fait
    if (!store.state.login.isLoggedIn) {
      await store.dispatch('login/initializeAuth')
    }
    
    next()
  } catch (error) {
    console.error('Erreur lors de l\'initialisation de l\'authentification:', error)
    next()
  }
}

/**
 * Intercepteur pour gérer les erreurs d'authentification globales
 */
export const handleAuthErrors = (error) => {
  if (error.message && error.message.includes('Session expirée')) {
    // Rediriger vers la page de login
    store.dispatch('login/logout')
    
    // Si on est dans un contexte de router, rediriger
    if (window.location.pathname !== '/login') {
      window.location.href = '/login'
    }
  }
}

// Exporter une fonction pour configurer les guards sur le router
export const setupAuthGuards = () => {
  // Intercepteur global pour les erreurs
  window.addEventListener('unhandledrejection', (event) => {
    if (event.reason && typeof event.reason.message === 'string') {
      handleAuthErrors(event.reason)
    }
  })
}
